/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  InputAdornment
} from '@mui/material';

import { Helmet } from 'react-helmet';
import PerfectScrollbar from 'react-perfect-scrollbar';
import debounce from 'lodash/debounce';
import { useToast } from '@context/toastContext';
import moment from 'moment';

import { getErrorMessage } from '@api/handleApiError';
// constant
import { OrderStatus, OrderStatusConfig } from '@utils/constants';
// component
import UILoader from '@components/UILoader';
import { searchOrdersAPI } from '@api/main';
import { DateRangePicker } from '@mui/lab';

const rowsPerPageOptions = [10, 20, 50];

const Order = () => {
  const showToast = useToast();
  const [orders, setOrders] = useState();
  const [totalRows, setTotalRows] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [orderCode, setOrderCode] = useState('');
  const [orderStatus, setOrderStatus] = useState(0);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [date, setDate] = useState([moment().startOf('month').valueOf(), new Date().valueOf()]);
  const statusId = orderStatus === 0 ? null : orderStatus;
  const [searchFilter, setSearchFilter] = useState({
    orderCode,
    fromDate: date[0],
    toDate: date[1],
    fromAmount,
    toAmount,
    orderStatus: statusId
  });

  const handleSearch = () => {
    if (toAmount < fromAmount) {
      setToAmount(fromAmount * 1 + 300000);
    }
    setSearchFilter({
      orderCode,
      fromDate: date[0],
      toDate: date[1],
      fromAmount,
      toAmount,
      orderStatus: statusId
    });
    setPage(0);
  };
  const handleKeywordChange = (event) => {
    setOrderCode(event.target.value);
  };
  const handleFromAmountChange = (event) => {
    setFromAmount(event.target.value);
    setToAmount(event.target.value * 1 + 300000);
  };
  const handleToAmountChange = (event) => {
    setToAmount(event.target.value);
  };
  const handleStatusChange = (e) => {
    setOrderStatus(e.target.value);
  };

  const handleClear = () => {
    setSearchFilter({
      orderCode: '',
      fromDate: moment().startOf('month').valueOf(),
      toDate: new Date().valueOf(),
      fromAmount: null,
      toAmount: null
    });
    setOrderCode('');
    setDate([moment().startOf('month').valueOf(), new Date().valueOf()]);
    setFromAmount('');
    setToAmount('');
    setOrderStatus('');
    setPage(0);
  };

  const handleLimitChange = (event) => {
    setPage(0);
    setRowPerPage(event.target.value);
    setRefreshToggle(!refreshToggle);
  };

  const handlePageChange = (event, page) => {
    setPage(page);
    fetchData(rowPerPage, page + 1, searchFilter);
  };

  const fetchData = useCallback(
    debounce(async (rowPerPage, page, searchFilter) => {
      try {
        setIsLoading(true);
        const result = await searchOrdersAPI(rowPerPage, page, searchFilter);
        setOrders(result.data.pageData);
        setTotalRows(result.data.paging.totalItem);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        showToast({
          type: 'error',
          message: getErrorMessage(error)
        });
      }
    }, 400),
    []
  );
  useEffect(() => {
    fetchData(rowPerPage, page + 1, searchFilter);
  }, [refreshToggle, searchFilter]);

  const handleSearchInputChange = (value) => {
    setDate([moment(value[0]).startOf('day').valueOf(), moment(value[1]).endOf('day').valueOf()]);
  };
  return (
    <>
      <Helmet>
        <title>Orders | Supper Admin</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <UILoader open={isLoading} />
        <Container maxWidth={false}>
          <Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  flex: 1
                }}
              >
                Đơn Hàng
              </Typography>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item md={3} xs={12}>
                      <DateRangePicker
                        startText="Ngày"
                        endText="Ngày"
                        value={date}
                        onChange={handleSearchInputChange}
                        inputFormat="dd/MM/yyyy"
                        renderInput={({ inputProps, ...startProps }, endProps) => {
                          const startValue = inputProps.value;
                          delete inputProps.value;
                          return (
                            <TextField
                              fullWidth
                              {...startProps}
                              inputProps={inputProps}
                              value={`${startValue} - ${endProps.inputProps.value}`}
                            />
                          );
                        }}
                      />
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        onChange={handleStatusChange}
                        value={orderStatus}
                        select
                        fullWidth
                        label="Trạng Thái"
                        variant="outlined"
                      >
                        <MenuItem value={0}>Tất Cả</MenuItem>
                        {OrderStatus.map((i, index) => (
                          <MenuItem key={index} value={i.value}>
                            {i.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <TextField
                        fullWidth
                        onChange={handleKeywordChange}
                        value={orderCode}
                        label="Mã Hóa Đơn"
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item md={3} xs={12} />
                    <Grid item md={3} xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          type="number"
                          fullWidth
                          label="Khoảng Tiền Từ"
                          variant="outlined"
                          onChange={handleFromAmountChange}
                          value={fromAmount}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">VND</InputAdornment>
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                          error={toAmount * 1 < fromAmount * 1}
                          type="number"
                          helperText={toAmount * 1 < fromAmount * 1 ? `Giá trị này phải lớn hơn ${fromAmount}` : false}
                          fullWidth
                          label="Đến"
                          variant="outlined"
                          value={toAmount}
                          onChange={handleToAmountChange}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">VND</InputAdornment>
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item md={3} xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          py: 1
                        }}
                      >
                        <Button
                          color="primary"
                          variant="contained"
                          sx={{ mx: 1, height: '40px' }}
                          onClick={handleSearch}
                        >
                          Tìm kiếm
                        </Button>
                        <Button sx={{ height: '40px' }} color="primary" variant="outlined" onClick={handleClear}>
                          Xóa
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </Box>
          <Box sx={{ pt: 3 }}>
            <Card>
              <PerfectScrollbar>
                <Box>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mã Hóa Đơn</TableCell>
                        <TableCell>Người Dùng</TableCell>
                        <TableCell>Ngày Xuất Đơn</TableCell>
                        <TableCell>Ngày Cập Nhật</TableCell>
                        <TableCell>Tổng Tiền</TableCell>
                        <TableCell>Trạng Thái</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders?.map((i) => (
                        <TableRow hover key={i.orderCode}>
                          <TableCell>
                            <Typography variant="body2" sx={{ width: '200px' }} noWrap>
                              {i.orderCode}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ width: '150px' }} noWrap>
                              {i.userName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {moment(i.createAtUtcUnix).format('dd/MM/yyyy')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{moment(i.updateAtUtcUnix).format('dd/MM/yyyy')}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(i.totalAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={OrderStatusConfig[i.orderStatus]?.title}
                              color={OrderStatusConfig[i.orderStatus]?.color}
                              sx={{ fontWeight: 'bold', height: '25px', borderRadius: '6px' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </PerfectScrollbar>
              <TablePagination
                component="div"
                onPageChange={handlePageChange}
                page={page}
                count={totalRows}
                rowsPerPage={rowPerPage}
                onRowsPerPageChange={handleLimitChange}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </Card>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Order;
